import { t } from 'i18next'
import { useFormContext } from 'react-hook-form'

import { Render } from '@/components/render-if'
import { Button, RadioGroupField } from '@/components/ui'
import { ComboboxField } from '@/components/ui/combobox'
import { ConjugeUseCase } from '@/features/proposta/brasil/domain/Comprador/use-case/conjuge-use-case'
import type {
  CompradorPessoaFisicaStepSchemaInput,
  CompradorPessoaFisicaStepSchemaOutput,
} from '@/features/proposta/brasil/domain/schemas/compradores/comprador-pf.schema'
import {
  ESTADO_CIVIL_OPTIONS,
  REGIME_CASAMENTO_OPTIONS,
  UNIAO_ESTAVEL_OPTIONS,
} from '@/features/proposta/brasil/utils/constants'
import {
  StepPropostaContent,
  StepPropostaFooter,
  StepPropostaHeaderContent,
  StepPropostaHeaderTitle,
  StepPropostaRoot,
} from '@/features/proposta/shared/components/layouts/step-proposta'
import { useGuardedHandler } from '@/hooks/use-guarded-handlers'
import { EstadoCivilEnum } from '@/types/cliente'
import {
  ClienteRegimeCasamentoEnum,
  tipoAssinaturaEnum,
  type TClienteRegimeCasamentoEnum,
  type TTipoAssinaturaEnum,
} from '@/types/proposta'
import { checkOnlineGuard } from '@/utils'

type TEstadoCivilComprador = {
  onBack: () => void
  onNext: () => void
  onFinish: (value: CompradorPessoaFisicaStepSchemaOutput) => Promise<void>
  isLast: boolean
  nacionalidadePrincipalId?: string
}

const TIPO_ASSINATURA_OPTIONS = [
  {
    value: tipoAssinaturaEnum.Simples,
    label: t('proposta:cadastro_compradores.steps.estado_civil.tipo_assinatura.simples'),
  },
  {
    value: tipoAssinaturaEnum.Dupla,
    label: t('proposta:cadastro_compradores.steps.estado_civil.tipo_assinatura.dupla'),
  },
]

export const EstadoCivilComprador = ({
  onBack,
  isLast,
  onNext,
  onFinish,
  nacionalidadePrincipalId,
}: TEstadoCivilComprador) => {
  const prefix = 'proposta:cadastro_compradores.steps.estado_civil'

  const {
    control,
    watch,
    setValue,
    handleSubmit,
    trigger,
    formState: { isSubmitting },
  } = useFormContext<CompradorPessoaFisicaStepSchemaInput, unknown, CompradorPessoaFisicaStepSchemaOutput>()

  const estadoCivil = watch('estadoCivil.status')
  const regimeCasamento = watch('estadoCivil.regimeCasamento')
  const conjuge = watch('conjuge')
  const mode = watch('mode')
  const habilitarTipoAssinatura = (regime: TClienteRegimeCasamentoEnum) => {
    return [
      ClienteRegimeCasamentoEnum.ComunhaoUniversalBens,
      ClienteRegimeCasamentoEnum.ComunhaoParcialBens,
      ClienteRegimeCasamentoEnum.ParticipacaoFinalNosAquestos,
    ].includes(
      regime as Exclude<
        TClienteRegimeCasamentoEnum,
        'SeparacaoLegal' | 'SeparacaoTotalBens' | 'SeparacaoObrigatoriaBens' | 'SeparacaoConvencional'
      >,
    )
  }
  const handleConcluirCadastro = useGuardedHandler(
    [checkOnlineGuard()],
    async (formOutput: CompradorPessoaFisicaStepSchemaOutput) => await onFinish(formOutput),
  )

  const handleNext = async () => {
    if (isLast) {
      handleSubmit(handleConcluirCadastro)()
      return
    }
    if (!conjuge) {
      setValue('conjuge', ConjugeUseCase.inicializarDefaultValues(nacionalidadePrincipalId), { shouldDirty: false })
    }
    const isValid = await trigger('estadoCivil')
    if (!isValid) return
    onNext()
  }

  const handleMudarTipoAssinatura = (valorSelecionado: TTipoAssinaturaEnum) => {
    const tipoAssinatura = watch('estadoCivil.tipoAssinatura')
    const tipoAssinaturaSimplesOuInexistente = tipoAssinatura === 'Simples' || !tipoAssinatura
    const mudouAssinaturaParaDupla =
      mode === 'edit' && tipoAssinaturaSimplesOuInexistente && valorSelecionado === 'Dupla'
    const mudouAssinaturaParaSimples = tipoAssinatura === 'Dupla' && valorSelecionado === 'Simples'

    if (mudouAssinaturaParaDupla && !conjuge) {
      setValue('mode', 'create')
      setValue('editouTipoAssinatura', true)
    }
    if (mudouAssinaturaParaSimples) setValue('conjuge', null, { shouldDirty: false })

    setValue('estadoCivil.tipoAssinatura', valorSelecionado, { shouldDirty: false })
  }

  return (
    <StepPropostaRoot>
      <StepPropostaHeaderContent>
        <StepPropostaHeaderTitle>{t(`${prefix}.titulo`)}</StepPropostaHeaderTitle>
      </StepPropostaHeaderContent>
      <StepPropostaContent hasBorder>
        <div className="w-full">
          <ComboboxField
            className="sm:w-90"
            control={control}
            placeholder={t(`${prefix}.placeholder.selecione`)}
            label={t(`${prefix}.label.estado_civil`)}
            name="estadoCivil.status"
            valueProperty="value"
            labelProperty="label"
            hideClearButton
            showSearch={false}
            options={ESTADO_CIVIL_OPTIONS}
            onSelect={() => {
              setValue('estadoCivil.uniaoEstavel', null)
              setValue('estadoCivil.regimeCasamento', null)
            }}
          />
          <Render.If condition={estadoCivil && estadoCivil !== EstadoCivilEnum.CivilCasado}>
            {() => (
              <RadioGroupField
                name="estadoCivil.uniaoEstavel"
                control={control}
                groupLabel={t(`${prefix}.label.uniao_estavel`)}
                options={UNIAO_ESTAVEL_OPTIONS}
              />
            )}
          </Render.If>
          <Render.If condition={estadoCivil && estadoCivil === EstadoCivilEnum.CivilCasado}>
            {() => (
              <>
                <ComboboxField
                  className="sm:w-90"
                  control={control}
                  placeholder={t(`${prefix}.placeholder.selecione`)}
                  label={t(`${prefix}.label.regime_casamento`)}
                  name="estadoCivil.regimeCasamento"
                  valueProperty="value"
                  labelProperty="label"
                  hideClearButton
                  showSearch={false}
                  options={REGIME_CASAMENTO_OPTIONS}
                  onSelect={(option) => {
                    if (!habilitarTipoAssinatura(option.value)) {
                      setValue('conjuge', null)
                    }
                    setValue('estadoCivil.tipoAssinatura', null)
                  }}
                />
                <Render.If condition={regimeCasamento && habilitarTipoAssinatura(regimeCasamento)}>
                  {() => (
                    <RadioGroupField
                      name="estadoCivil.tipoAssinatura"
                      control={control}
                      groupLabel={t(`${prefix}.label.tipo_assinatura`)}
                      options={TIPO_ASSINATURA_OPTIONS}
                      onValueChange={handleMudarTipoAssinatura}
                    />
                  )}
                </Render.If>
              </>
            )}
          </Render.If>
        </div>
      </StepPropostaContent>
      <StepPropostaFooter>
        <Button variant="outline" onClick={onBack} className="font-nunito h-12 w-full md:w-auto">
          {t('proposta:cadastro_compradores.steps.voltar')}
        </Button>
        <Button
          className="font-nunito h-12 w-full md:w-27"
          onClick={handleNext}
          disabled={isSubmitting}
          isLoading={isSubmitting}
          showLoadingText={false}
        >
          {t(isLast ? 'proposta:cadastro_compradores.steps.concluir' : 'proposta:cadastro_compradores.steps.proximo')}
        </Button>
      </StepPropostaFooter>
    </StepPropostaRoot>
  )
}
